import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type TeamList,
  organizationsAccessListOptions,
  organizationsTeamsCreateMutation,
  organizationsTeamsDestroyMutation,
  organizationsTeamsListOptions,
  organizationsTeamsListQueryKey,
  organizationsTeamsPartialUpdateMutation
} from 'src/api/main'
import {
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import { useWorkspaceRoute } from '../../pages/WorkspacePageShell'

/* ------------------------------------------------------------------ */
/*  Shared types                                                       */
/* ------------------------------------------------------------------ */

export type TeamWithHead = TeamList & {
  head_user?: number | null
  head_user_detail?: {
    id: number
    email: string
    first_name: string
    last_name: string
  } | null
  description?: string
}

export type EntityFormValues = {
  name: string
  description?: string
  abbreviation?: string
  head_user?: number | null
  parent?: number | null
}

const abbreviationRules = (t: (key: string) => string) => [
  {
    pattern: /^[A-Z]{2,}$/,
    message: t('workspace.orgChart.fields.abbreviationHelp')
  }
]

/* ------------------------------------------------------------------ */
/*  Shared form fields                                                 */
/* ------------------------------------------------------------------ */

export const EntityFormFields = ({
  t,
  memberOptions,
  parentLabel,
  parentOptions
}: {
  t: (key: string) => string
  memberOptions: { label: string; value: number }[]
  parentLabel?: string
  parentOptions?: { label: string; value: number }[]
}) => (
  <>
    <Form.Item
      name="name"
      label={t('workspace.orgChart.fields.name')}
      rules={[{ required: true, message: t('required_field_error') }]}
    >
      <Input placeholder={t('placeholders.name')} />
    </Form.Item>
    <Form.Item
      name="abbreviation"
      label={t('workspace.orgChart.fields.abbreviation')}
      rules={abbreviationRules(t)}
    >
      <Input placeholder={t('placeholders.abbreviation')} maxLength={10} />
    </Form.Item>
    <Form.Item
      name="description"
      label={t('workspace.orgChart.fields.description')}
    >
      <Input placeholder={t('placeholders.description')} />
    </Form.Item>
    <Form.Item name="head_user" label={t('workspace.orgChart.fields.owner')}>
      <Select
        allowClear
        showSearch={{ optionFilterProp: 'label' }}
        placeholder={t('workspace.orgChart.fields.ownerPlaceholder')}
        options={memberOptions}
      />
    </Form.Item>
    {parentLabel && parentOptions ? (
      <Form.Item name="parent" label={parentLabel}>
        <Select
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          options={parentOptions}
        />
      </Form.Item>
    ) : null}
  </>
)

/* ------------------------------------------------------------------ */
/*  Divisions (type = 'branch')                                       */
/* ------------------------------------------------------------------ */

export const OrgDivisionsPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [createForm] = Form.useForm<EntityFormValues>()
  const [editForm] = Form.useForm<EntityFormValues>()

  const {
    data: divisionsPage,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['branch'], per_page: 200 }
    }),
    enabled: idValid
  })

  const divisions = (divisionsPage?.results ?? []) as TeamWithHead[]

  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (accessPage?.results ?? []).map((a) => ({
        label:
          `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.email,
        value: a.user.id
      })),
    [accessPage]
  )

  const memberLookup = useMemo(() => {
    const map = new Map<number, string>()
    for (const opt of memberOptions) {
      map.set(opt.value, opt.label)
    }
    return map
  }, [memberOptions])

  const invalidateTeams = async () => {
    await queryClient.invalidateQueries({
      queryKey: organizationsTeamsListQueryKey()
    })
  }

  const createMutation = useMutation({
    ...organizationsTeamsCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.divisionAdded'))
      setCreateOpen(false)
      createForm.resetFields()
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.divisionUpdated'))
      setEditOpen(false)
      setEditingId(null)
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    ...organizationsTeamsDestroyMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.divisionDeleted'))
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const openEdit = (row: TeamWithHead) => {
    setEditingId(row.id)
    editForm.setFieldsValue({
      name: row.name,
      abbreviation: row.abbreviation ?? undefined,
      description: row.description ?? undefined,
      head_user: row.head_user ?? undefined
    })
    setEditOpen(true)
  }

  const columns: ColumnsType<TeamWithHead> = [
    {
      fixed: 'left',
      title: t('workspace.orgChart.columns.name'),
      dataIndex: 'name'
    },
    {
      title: t('workspace.orgChart.columns.owner'),
      key: 'head_user',
      render: (_, row) => {
        if (row.head_user_detail) {
          const d = row.head_user_detail

          return `${d.first_name} ${d.last_name}`.trim() || d.email
        }
        return row.head_user ? (memberLookup.get(row.head_user) ?? '—') : '—'
      }
    },
    {
      title: t('workspace.orgChart.columns.members'),
      key: 'users_count',
      render: (_, row) => row.users_count
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 260,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            {t('workspace.orgChart.actions.edit')}
          </Button>
          <Button
            type="link"
            onClick={() =>
              navigate(
                paths.workspaces.membersDivision(
                  workspaceRouteId,
                  String(row.id)
                )
              )
            }
          >
            {t('workspace.orgChart.actions.manage')}
          </Button>
          <Popconfirm
            title={t('workspace.orgChart.divisions.deleteConfirm')}
            okButtonProps={{ loading: deleteMutation.isPending }}
            onConfirm={() =>
              void deleteMutation.mutateAsync({ path: { id: row.id } })
            }
          >
            <Button type="link" danger>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}

      <ProTableContainer<TeamWithHead>
        showSearch={false}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        columns={columns}
        dataSource={divisions}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.membersTeams.listTitleWithCount', {
                label: t('workspace.membersTeams.tabs.divisions'),
                count: divisionsPage?.count ?? 0
              })}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.divisions.hint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              createForm.resetFields()
              setCreateOpen(true)
            }}
          >
            {t('workspace.orgChart.divisions.add')}
          </Button>
        }
      />

      {/* Create drawer */}
      <Drawer
        title={t('workspace.orgChart.divisions.drawerTitle')}
        size={480}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setCreateOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={createMutation.isPending}
              onClick={() => createForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => {
            createMutation.mutate({
              body: {
                name: values.name,
                abbreviation: values.abbreviation,
                description: values.description,
                head_user: values.head_user ?? null,
                type: 'branch',
                organization: workspaceId
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields t={t} memberOptions={memberOptions} />
        </Form>
      </Drawer>

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.divisions.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (!editingId) return
            updateMutation.mutate({
              path: { id: editingId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields t={t} memberOptions={memberOptions} />
        </Form>
      </Drawer>
    </Flex>
  )
}

/* ------------------------------------------------------------------ */
/*  Departments (type = 'department')                                  */
/* ------------------------------------------------------------------ */

export const OrgDepartmentsPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [createForm] = Form.useForm<EntityFormValues>()
  const [editForm] = Form.useForm<EntityFormValues>()

  const {
    data: departmentsPage,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['department'], per_page: 200 }
    }),
    enabled: idValid
  })

  const departments = (departmentsPage?.results ?? []) as TeamWithHead[]

  // Load divisions for parent select
  const { data: divisionsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['branch'], per_page: 200 }
    }),
    enabled: idValid
  })

  const divisionOptions = useMemo(
    () =>
      (divisionsPage?.results ?? []).map((d) => ({
        label: d.name,
        value: d.id
      })),
    [divisionsPage]
  )

  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (accessPage?.results ?? []).map((a) => ({
        label:
          `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.email,
        value: a.user.id
      })),
    [accessPage]
  )

  const memberLookup = useMemo(() => {
    const map = new Map<number, string>()
    for (const opt of memberOptions) {
      map.set(opt.value, opt.label)
    }
    return map
  }, [memberOptions])

  const invalidateTeams = async () => {
    await queryClient.invalidateQueries({
      queryKey: organizationsTeamsListQueryKey()
    })
  }

  const createMutation = useMutation({
    ...organizationsTeamsCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.departmentAdded'))
      setCreateOpen(false)
      createForm.resetFields()
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.departmentUpdated'))
      setEditOpen(false)
      setEditingId(null)
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    ...organizationsTeamsDestroyMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.departmentDeleted'))
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const parentLookup = useMemo(() => {
    const map = new Map<number, string>()
    for (const d of divisionsPage?.results ?? []) {
      map.set(d.id, d.name)
    }
    return map
  }, [divisionsPage])

  const openEdit = (row: TeamWithHead) => {
    setEditingId(row.id)
    editForm.setFieldsValue({
      name: row.name,
      abbreviation: row.abbreviation ?? undefined,
      description: row.description ?? undefined,
      head_user: row.head_user ?? undefined,
      parent: row.parent ?? undefined
    })
    setEditOpen(true)
  }

  const columns: ColumnsType<TeamWithHead> = [
    {
      fixed: 'left',
      title: t('workspace.orgChart.columns.name'),
      dataIndex: 'name'
    },
    {
      title: t('workspace.orgChart.columns.division'),
      key: 'parent',
      render: (_, row) =>
        row.parent ? (parentLookup.get(row.parent) ?? '—') : '—'
    },
    {
      title: t('workspace.orgChart.columns.owner'),
      key: 'head_user',
      render: (_, row) => {
        if (row.head_user_detail) {
          const d = row.head_user_detail

          return `${d.first_name} ${d.last_name}`.trim() || d.email
        }
        return row.head_user ? (memberLookup.get(row.head_user) ?? '—') : '—'
      }
    },
    {
      title: t('workspace.orgChart.columns.members'),
      key: 'users_count',
      render: (_, row) => row.users_count
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 260,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            {t('workspace.orgChart.actions.edit')}
          </Button>
          <Button
            type="link"
            onClick={() =>
              navigate(
                paths.workspaces.membersDepartment(
                  workspaceRouteId,
                  String(row.id)
                )
              )
            }
          >
            {t('workspace.orgChart.actions.manage')}
          </Button>
          <Popconfirm
            title={t('workspace.orgChart.departments.deleteConfirm')}
            okButtonProps={{ loading: deleteMutation.isPending }}
            onConfirm={() =>
              void deleteMutation.mutateAsync({ path: { id: row.id } })
            }
          >
            <Button type="link" danger>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}

      <ProTableContainer<TeamWithHead>
        showSearch={false}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        columns={columns}
        dataSource={departments}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.membersTeams.listTitleWithCount', {
                label: t('workspace.membersTeams.tabs.departments'),
                count: departmentsPage?.count ?? 0
              })}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.departments.hint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              createForm.resetFields()
              setCreateOpen(true)
            }}
          >
            {t('workspace.orgChart.departments.add')}
          </Button>
        }
      />

      {/* Create drawer */}
      <Drawer
        title={t('workspace.orgChart.departments.drawerTitle')}
        size={480}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setCreateOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={createMutation.isPending}
              onClick={() => createForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => {
            createMutation.mutate({
              body: {
                name: values.name,
                abbreviation: values.abbreviation,
                description: values.description,
                head_user: values.head_user ?? null,
                type: 'department',
                parent: values.parent ?? null,
                organization: workspaceId
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.parentDivision')}
            parentOptions={divisionOptions}
          />
        </Form>
      </Drawer>

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.departments.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (!editingId) return
            updateMutation.mutate({
              path: { id: editingId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null,
                parent: values.parent ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.parentDivision')}
            parentOptions={divisionOptions}
          />
        </Form>
      </Drawer>
    </Flex>
  )
}

/* ------------------------------------------------------------------ */
/*  Teams (type = 'section' or no specific type filter)                */
/* ------------------------------------------------------------------ */

export const OrgTeamsPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [createForm] = Form.useForm<EntityFormValues>()
  const [editForm] = Form.useForm<EntityFormValues>()

  const {
    data: teamsPage,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['section'], per_page: 200 }
    }),
    enabled: idValid
  })

  const teams = (teamsPage?.results ?? []) as TeamWithHead[]

  // Load departments for parent select
  const { data: departmentsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['department'], per_page: 200 }
    }),
    enabled: idValid
  })

  const departmentOptions = useMemo(
    () =>
      (departmentsPage?.results ?? []).map((d) => ({
        label: d.name,
        value: d.id
      })),
    [departmentsPage]
  )

  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (accessPage?.results ?? []).map((a) => ({
        label:
          `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.email,
        value: a.user.id
      })),
    [accessPage]
  )

  const memberLookup = useMemo(() => {
    const map = new Map<number, string>()
    for (const opt of memberOptions) {
      map.set(opt.value, opt.label)
    }
    return map
  }, [memberOptions])

  const invalidateTeams = async () => {
    await queryClient.invalidateQueries({
      queryKey: organizationsTeamsListQueryKey()
    })
  }

  const createMutation = useMutation({
    ...organizationsTeamsCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.teamAdded'))
      setCreateOpen(false)
      createForm.resetFields()
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.teamUpdated'))
      setEditOpen(false)
      setEditingId(null)
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    ...organizationsTeamsDestroyMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.teamDeleted'))
      await invalidateTeams()
    },
    onError: (e) => showApiErrors(e)
  })

  const parentLookup = useMemo(() => {
    const map = new Map<number, string>()
    for (const d of departmentsPage?.results ?? []) {
      map.set(d.id, d.name)
    }
    return map
  }, [departmentsPage])

  const openEdit = (row: TeamWithHead) => {
    setEditingId(row.id)
    editForm.setFieldsValue({
      name: row.name,
      abbreviation: row.abbreviation ?? undefined,
      description: row.description ?? undefined,
      head_user: row.head_user ?? undefined,
      parent: row.parent ?? undefined
    })
    setEditOpen(true)
  }

  const columns: ColumnsType<TeamWithHead> = [
    {
      fixed: 'left',
      title: t('workspace.orgChart.columns.name'),
      dataIndex: 'name'
    },
    {
      title: t('workspace.orgChart.columns.department'),
      key: 'parent',
      render: (_, row) =>
        row.parent ? (parentLookup.get(row.parent) ?? '—') : '—'
    },
    {
      title: t('workspace.orgChart.columns.owner'),
      key: 'head_user',
      render: (_, row) => {
        if (row.head_user_detail) {
          const d = row.head_user_detail

          return `${d.first_name} ${d.last_name}`.trim() || d.email
        }
        return row.head_user ? (memberLookup.get(row.head_user) ?? '—') : '—'
      }
    },
    {
      title: t('workspace.orgChart.columns.members'),
      key: 'users_count',
      render: (_, row) => row.users_count
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 260,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            {t('workspace.orgChart.actions.edit')}
          </Button>
          <Button
            type="link"
            onClick={() =>
              navigate(
                paths.workspaces.membersTeam(workspaceRouteId, String(row.id))
              )
            }
          >
            {t('workspace.orgChart.actions.manage')}
          </Button>
          <Popconfirm
            title={t('workspace.orgChart.teams.deleteConfirm')}
            okButtonProps={{ loading: deleteMutation.isPending }}
            onConfirm={() =>
              void deleteMutation.mutateAsync({ path: { id: row.id } })
            }
          >
            <Button type="link" danger>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}

      <ProTableContainer<TeamWithHead>
        showSearch={false}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        columns={columns}
        dataSource={teams}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.membersTeams.listTitleWithCount', {
                label: t('workspace.membersTeams.tabs.teams'),
                count: teamsPage?.count ?? 0
              })}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.teams.hint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              createForm.resetFields()
              setCreateOpen(true)
            }}
          >
            {t('workspace.orgChart.teams.add')}
          </Button>
        }
      />

      {/* Create drawer */}
      <Drawer
        title={t('workspace.orgChart.teams.drawerTitle')}
        size={480}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setCreateOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={createMutation.isPending}
              onClick={() => createForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => {
            createMutation.mutate({
              body: {
                name: values.name,
                abbreviation: values.abbreviation,
                description: values.description,
                head_user: values.head_user ?? null,
                type: 'section',
                parent: values.parent ?? null,
                organization: workspaceId
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.department')}
            parentOptions={departmentOptions}
          />
        </Form>
      </Drawer>

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.teams.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (!editingId) return
            updateMutation.mutate({
              path: { id: editingId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null,
                parent: values.parent ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.department')}
            parentOptions={departmentOptions}
          />
        </Form>
      </Drawer>
    </Flex>
  )
}
